import React, { useRef, useState } from "react";
import type * as types from "../PhoenixFormsV4";
import type * as controls from "../PhoenixFormsV4.Controls";
import type * as appData from "../PhoenixFormsV4.AppData";
import PNXLabel from "./PNXLabel";
import { usePhoenixForms } from "../PhoenixFormsProvider";
import { FormFileControlData, FormFileResourceMetadata } from "../PhoenixFormsV4.Models";
import { usePhoenixFormsDialog } from "./usePhoenixFormsDialog";
import { phoenixDownloadFile } from "tools/fileDownloadTools";
import Button from "@mui/material/Button";

const restrictedExtensions = [
  "exe",
  "com",
  "scr",
  "dll",
  "ocx",
  "bat",
  "cmd",
  "vbs",
  "js",
  "ps1",
  "pif",
  "reg",
  "sh",
  "msi",
  "cpl",
  "jar",
];

const photoExtensions = [
  "jpg",
  "jpeg",
  "jpe",
  "png",
  "gif",
  "bmp",
  "webp",
  "heic",
  "heics",
  "heif",
  "heifs",
  "tiff",
  "tif"
];

interface Props {
  controlProps: controls.FormControl;
  proxy: types.PhoenixFormsProxy;
  webRuntimeData: appData.WebRuntimeAppData;
  readonly?: boolean;
};

interface FilePhotoViewerProps {
  photos: FormFileResourceMetadata[];
  webRuntimeData: appData.WebRuntimeAppData;
  readonly: boolean;
  onDownload: (file: FormFileResourceMetadata) => void;
  onDelete: (file: FormFileResourceMetadata) => void;
}

const FilePhotoViewer = ({ photos, webRuntimeData, readonly, onDownload, onDelete }: FilePhotoViewerProps) => {
  const dialog = usePhoenixFormsDialog();
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState<number>(0);

  const currentPhoto = photos[currentPhotoIndex];
  const imageUrl = `${webRuntimeData.host}/services/forms4/${webRuntimeData.fileId}/phoenix-editor/${webRuntimeData.sessionKey}/formFiles/${currentPhoto.id}`;

  return (
    <div className="photopreview">
      <div className="preview">
        <div className="switch">
          <Button
            onClick={() => setCurrentPhotoIndex(currentPhotoIndex - 1)}
            disabled={currentPhotoIndex === 0}
          >
            &lt;
          </Button>
        </div>
        <div className="photo">
          <img src={imageUrl} alt={currentPhoto.name} />
        </div>
        <div className="switch">
          <Button
            onClick={() => setCurrentPhotoIndex(currentPhotoIndex + 1)}
            disabled={currentPhotoIndex === photos.length - 1}
          >
            &gt;
          </Button>
        </div>
      </div>
      <div className="button-row">
        <div className="left">
          <Button variant="contained" color="primary" onClick={() => onDownload(currentPhoto)}>
            Download
          </Button>
          {!readonly && (
          <Button variant="contained" color="secondary" onClick={() => onDelete(currentPhoto)}>
            Delete
          </Button>
          )}
        </div>
        <div className="right">
          <Button variant="contained" onClick={() => dialog.hide()}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * This control is used for both 'File' and 'Document' types, the difference is handled in the UI by using 
 * properties coming from the compiled forms data.
 */
const PhoenixFileControl = ({
  controlProps,
  proxy,
  webRuntimeData,
  readonly,
} : Props) => {

  const { isPreview } = usePhoenixForms();
  const dialog  = usePhoenixFormsDialog();
  const uploader = useRef<HTMLInputElement>(null);
  const [fileData, setFileData] = useState<FormFileControlData>(proxy.requestFormFileData(controlProps.id));

  const required = controlProps.required && fileData.files.length === 0;

  const refreshFileData = () => {
    setFileData(proxy.requestFormFileData(controlProps.id));
  };

  const onUploadClick = () => {
    uploader.current.value = "";
    uploader.current.click();
  };

  const uploaderOnChange = () => {
    if (!uploader.current.files.length) return;
    const file = uploader.current.files[0];

    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (restrictedExtensions.includes(fileExtension)) {
      dialog.showError(`The file with extension ".${fileExtension}" is not allowed for upload. Please choose a different file.`);
      return;
    }

    if (fileData.isPhotoEnabled && photoExtensions.includes(fileExtension)) {
      // this is a photo, but the control is configured to allow photos, so we allow it to be uploaded without checking the allowedExtensions list
    }
    else if (fileData.allowedExtensions.length && !fileData.allowedExtensions.includes(fileExtension)) {
      const andImages = fileData.isPhotoEnabled ? " and image files" : "";
      dialog.showError(`Files with extension ".${fileExtension}" are not allowed for upload. Allowed extensions are: ${fileData.allowedExtensions.join(", ")}${andImages}.`);
      return;
    }

    dialog.showLoader("Uploading File...");

    const xmlhttp = new XMLHttpRequest();
    xmlhttp.open("POST", `${webRuntimeData.host}/services/forms4/${webRuntimeData.fileId}/phoenix-editor/${webRuntimeData.sessionKey}/formFiles?fileName=${encodeURIComponent(file.name)}`, true);

    let complete = false;
    xmlhttp.onreadystatechange = () => {
      if (xmlhttp.readyState === 4 && !complete) {
          complete = true;

          if (xmlhttp.status !== 0) {
            dialog.hide();

            if (xmlhttp.status === 200) {
              const meta = JSON.parse(xmlhttp.responseText);
              proxy.fileAdd(controlProps.id, meta);
              refreshFileData();
            }
            else {
              dialog.showError("Error uploading file");
            }
          }
      }
    };

    xmlhttp.setRequestHeader("Content-Type", "application/octet-stream");
    xmlhttp.send(file);
  };

  const onDownloadClick = (file: FormFileResourceMetadata) => {
    dialog.showLoader(`Downloading ${file.name}...`);
    phoenixDownloadFile(`/services/forms4/${webRuntimeData.fileId}/phoenix-editor/${webRuntimeData.sessionKey}/formFiles/${file.id}`, {
      completeCallback: () => {
        dialog.hide();
      },
      fileNameOverride: file.name
    });
  };

  const onPhotoClick = (photos: FormFileResourceMetadata[]) => {
    if (photos.length === 0) return; // do nothing if no photos
    
    dialog.show(
      <FilePhotoViewer
        photos={photos}
        webRuntimeData={webRuntimeData}
        readonly={readonly}
        onDownload={onDownloadClick}
        onDelete={onPhotoDeleteClick}
      />
    );
  };

  const onPhotoDeleteClick = (file: FormFileResourceMetadata) => {
    dialog.hide(); // Hide the viewer dialog first
    dialog.confirmDelete(file.name, () => {
      proxy.fileDelete(controlProps.id, file.id);
      refreshFileData();
    });
  };

  const onDeleteClick = (file: FormFileResourceMetadata) => {
    dialog.confirmDelete(file.name, () => {
      proxy.fileDelete(controlProps.id, file.id);
      refreshFileData();
    });
  };

  let accept = undefined;
  if (fileData.allowedExtensions.length) {
    accept = fileData.allowedExtensions.map(ext => `.${ext}`).join(",");

    if (fileData.isPhotoEnabled) {
      accept += "," + photoExtensions.map(ext => `.${ext}`).join(",");
    }
  }

  let fileList = [];
  let photoList = [];
  if (fileData.isPhotoEnabled) {
    // Split files into photos and other files
    photoList = fileData.files.filter(file => {
      const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";
      return photoExtensions.includes(fileExtension);
    });
    
    fileList = fileData.files.filter(file => {
      const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";
      return !photoExtensions.includes(fileExtension);
    });
  }
  else {
    fileList = fileData.files;
  }
  
  return <div>
    <div className="file-header">
      <PNXLabel helpTopicId={!readonly && controlProps.topicId}>{controlProps.label}</PNXLabel>
      <div className="file-options-container">
        <div className="file-options" onClick={ !isPreview && !readonly ? onUploadClick : undefined }></div>
        {photoList.length > 0 && (
          <div className="photo-options" onClick={() => onPhotoClick(photoList)}>
            <div className="image-container">
              <img src={`${webRuntimeData.host}/services/forms4/${webRuntimeData.fileId}/phoenix-editor/${webRuntimeData.sessionKey}/formFiles/${photoList[0].id}`} alt={photoList[0].name} />
              <div className="image-count">{photoList.length}</div>
            </div>
          </div>
        )}
      </div>
      <input 
        type="file" 
        className="uploader" 
        ref={uploader}
        onChange={uploaderOnChange}
        accept={accept}
      />
    </div>
    {required && <div className="phoenix-validation">{"[ * Required ]"}</div>}
    {fileList.length > 0 && 
    <div className="file-list">
      {fileList.map(file => (
        <div key={file.id} className="file-item">
          <div className="file-item-name" onClick={() => onDownloadClick(file)}>{file.name} :: {(Math.round((file.fileSize / 1024) * 100) / 100).toLocaleString("en-US")} KB</div>
          {!readonly && <div className="file-item-options" onClick={() => onDeleteClick(file)}><i className="md-icon material-icons">delete</i></div>}
        </div>
      ))}
    </div>}
  </div>;
};

export default PhoenixFileControl;